import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import ServiceTitanCredentials from "@/modules/hub/features/CompanyManagement/components/ServiceTitanCredentials/ServiceTitanCredentials";
import ServiceTitanBusinessUnitMapping from "@/modules/hub/features/CompanyManagement/components/ServiceTitanBusinessUnitMapping/ServiceTitanBusinessUnitMapping";
import { useServiceTitanIntegration } from "@/modules/hub/features/CompanyManagement/hooks/useServiceTitanIntegration";
import ConfigurationStep from "../ConfigurationStep/ConfigurationStep";

interface ServiceTitanConfigurationProps {
  companyUuid: string;
}

const ServiceTitanConfiguration: React.FC<ServiceTitanConfigurationProps> = ({
  companyUuid,
}) => {
  const state = useServiceTitanIntegration(companyUuid);
  const {
    businessUnits,
    credential,
    credentialError,
    credentialLoading,
    credentialsSaveError,
    incompleteMappings,
    mappings,
  } = state;

  if (credentialLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500 dark:text-gray-400">
        <RefreshCw className="h-4 w-4 animate-spin" />
        Loading ServiceTitan credentials...
      </div>
    );
  }

  if (credentialError) {
    return (
      <div className="my-5 flex gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm dark:border-red-800 dark:bg-red-900/20">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600 dark:text-red-400" />
        <span className="text-red-700 dark:text-red-400">
          <span className="font-medium text-red-800 dark:text-red-300">
            Connection Error:{" "}
          </span>
          {credentialError}
        </span>
      </div>
    );
  }

  return (
    <>
      {credentialsSaveError && (
        <div className="mt-5 flex gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm dark:border-red-800 dark:bg-red-900/20">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600 dark:text-red-400" />
          <span className="text-red-700 dark:text-red-400">
            <span className="font-medium text-red-800 dark:text-red-300">
              Credential Error:{" "}
            </span>
            {credentialsSaveError}
          </span>
        </div>
      )}

      <ConfigurationStep
        description="Verified credentials are used for both KPI sync and customer import."
        isComplete={!!credential?.tenantId}
        number={1}
        title="API credentials"
      >
        <ServiceTitanCredentials state={state} />
      </ConfigurationStep>

      <ConfigurationStep
        description={`${businessUnits.length} business units found. Map each to a trade category so KPIs and invoices are grouped correctly.`}
        isComplete={mappings.length > 0 && incompleteMappings === 0}
        number={2}
        title="Business unit mapping"
      >
        <ServiceTitanBusinessUnitMapping state={state} />
      </ConfigurationStep>
    </>
  );
};

export default ServiceTitanConfiguration;
